import React from 'react';
import { Sequence, Audio, staticFile, useCurrentFrame, interpolate, Easing } from 'remotion';
import { calcDurationWithAudio } from '../utils/duration-calculator';
import { resolveMouseTemplate } from '../utils/mouse-templates';
import SubtitleOverlay from '../components/subtitle-overlay';

const MOUSE_EASING = Easing.inOut(Easing.cubic);

/**
 * Frame-based mouse cursor for video overlay with easing.
 * Searches backwards to find current action, applies ease-in-out for decisive movement.
 */
function MouseOverlay({ actions, resolution }) {
  const frame = useCurrentFrame();
  if (!actions || actions.length === 0) return null;

  // Find the last action whose frame <= current frame (backwards search)
  let currentAction = actions[0];
  let nextAction = actions.length > 1 ? actions[1] : currentAction;

  for (let i = actions.length - 1; i >= 0; i--) {
    if (frame >= actions[i].frame) {
      currentAction = actions[i];
      nextAction = i < actions.length - 1 ? actions[i + 1] : currentAction;
      break;
    }
  }

  const startPos = resolveMouseTemplate(currentAction.action, resolution);
  const endPos = resolveMouseTemplate(nextAction.action, resolution);

  const startFrame = currentAction.frame;
  const endFrame = nextAction.frame > startFrame ? nextAction.frame : startFrame + 1;

  // Ease-in-out for natural, decisive mouse movement
  const x = interpolate(
    frame, [startFrame, endFrame],
    [startPos.x, endPos.x],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: MOUSE_EASING },
  );
  const y = interpolate(
    frame, [startFrame, endFrame],
    [startPos.y, endPos.y],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp', easing: MOUSE_EASING },
  );

  const isClicking = currentAction.click && frame >= currentAction.frame && frame < currentAction.frame + 10;

  return (
    <div style={{
      position: 'absolute', left: x, top: y,
      transform: `translate(-2px,-2px) scale(${isClicking ? 0.85 : 1})`,
      pointerEvents: 'none', zIndex: 9999,
      transition: isClicking ? 'transform 0.05s ease-out' : 'none',
    }}>
      <svg width="20" height="26" viewBox="0 0 16 22" style={{ filter: 'drop-shadow(1px 2px 3px rgba(0,0,0,0.6))' }}>
        <path d="M1,1 L1,18 L5,14 L8,20.5 L10.5,19.5 L7.5,13 L13,13 Z"
              fill="white" stroke="black" strokeWidth="1.1" strokeLinejoin="round"/>
      </svg>
    </div>
  );
}

/**
 * Computes overlay sequences (mouse, subtitles, audio) from scenario scenes.
 */
export default function OverlayLayer({ scenes, fps, resolution = { width: 1920, height: 1080 }, showSubtitles = true }) {
  let frameOffset = 0;
  const entries = scenes.map(scene => {
    const duration = scene.durationInFrames || calcDurationWithAudio(scene, fps);
    const entry = { scene, from: frameOffset, duration };
    frameOffset += duration;
    return entry;
  });

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 500 }}>
      {entries.map(({ scene, from, duration }, i) => (
        <React.Fragment key={scene.id || `overlay-${i}`}>
          {/* Mouse overlay */}
          {scene.mouse && (
            <Sequence from={from} durationInFrames={duration}>
              <MouseOverlay actions={scene.mouse} resolution={resolution} />
            </Sequence>
          )}
          {/* Subtitle overlay */}
          {showSubtitles && scene.narration && (
            <Sequence from={from} durationInFrames={duration}>
              <SubtitleOverlay text={scene.narration} />
            </Sequence>
          )}
          {/* Mouse click sounds */}
          {scene.mouse && scene.mouse.filter(a => a.click).map((action, ci) => (
            <Sequence key={`click-${scene.id || i}-${ci}`} from={from + action.frame} durationInFrames={12}>
              <Audio src={staticFile('audio/click.ogg')} volume={0.5} />
            </Sequence>
          ))}
          {/* Keyboard typing sound for code-typing scenes */}
          {scene.type === 'code-typing' && (
            <Sequence from={from} durationInFrames={duration}>
              <Audio src={staticFile('audio/typing-loop.ogg')} volume={0.3} loop />
            </Sequence>
          )}
          {/* Narration audio overlay */}
          {scene.audioFile && (
            <Sequence from={from} durationInFrames={duration}>
              <Audio src={staticFile(scene.audioFile)} volume={0.8} />
            </Sequence>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
